//@author: devin

import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import {
    ApplicationRef,
    ComponentRef,
    createComponent,
    EmbeddedViewRef,
    inject,
    Injectable,
    NgZone,
    outputBinding,
} from "@angular/core";
import { ServiceLoader } from "../../services/service.loader";
import { S25Util } from "../../util/s25-util";
import { LangService } from "../../services/lang.service";
import { HelpTopic } from "../s25-help/s25.help.service";
import { ModalDataTypes, ModalMap, ModalName } from "./modal.map";
import { jSith } from "../../util/jquery-replacement";
import { ModalConfig, S25ModalComponent } from "../s25-modal/s25.modal.component";

export interface ModalData {
    lang: any;
    isUnsupportedBrowser: boolean;
    closeModal(): void;
    callBack(): void;
    modalInstance: NgbModalRef;
    title: string;
    iconClass?: string;
    helpLink?: HelpTopic;
}

@Injectable({
    providedIn: "root",
})
export class ModalService {
    private static zone: NgZone = undefined;
    private static ngbModal: NgbModal = undefined;
    private static returnToFocusElement: any = null;
    private static queue: Promise<unknown>[] = [];
    private static openModals: Modal[] = [];

    appRef = inject(ApplicationRef);

    private static registerModal(modal: Modal) {
        this.openModals.push(modal);
    }

    private static unregisterModal(modal: Modal) {
        this.openModals = this.openModals.filter((p) => p !== modal);
    }

    public static modal<K extends ModalName>(modalType: K, data: ModalDataTypes[K] & Partial<ModalData>) {
        ModalService.zone = ModalService.zone || ServiceLoader.get(NgZone);
        ModalService.ngbModal = ModalService.ngbModal || ServiceLoader.get(NgbModal);
        ModalService.returnToFocusElement = document.activeElement;
        const { promise, resolve, reject } = S25Util.createPromise<void>();
        let opened = false;

        return ModalService.zone.run(() => {
            return LangService.getLang().then((lang) => {
                let component: any = typeof modalType === "string" ? ModalMap.map[modalType].controller : modalType;
                let modalRef: NgbModalRef = ModalService.ngbModal.open(component, {
                    animation: !S25Util.isInIframe,
                    keyboard: true,
                    size: ModalMap.map[modalType].size || "lg",
                    backdrop: modalType === "dialog" || (ModalMap.map[modalType].asDialog ? "static" : true),
                    windowClass: modalType || "generic-modal",
                });
                ModalService.registerModal({ close: modalRef.close, closed: promise });
                opened = true;

                if (modalRef.componentInstance) {
                    data ??= <any>{};
                    // TODO: stop modifying the passed-in object
                    if (modalType === "dialog") {
                        // not sure why type inference doesn't work here :thinking:
                        (<any>data).answer = 0;
                    }
                    data.title ??= ModalMap.map[modalType].title;
                    data.iconClass ??= ModalMap.map[modalType].iconClass;
                    data.lang = lang;
                    data.isUnsupportedBrowser = S25Util.isUnsupportedBrowser();
                    data.closeModal = (result?: any) => modalRef.close(result);
                    data.modalInstance = modalRef;
                    modalRef.componentInstance.data = data;
                }

                // ngb modal automatically focuses the first focusable element in the modal
                // but this causes a problem because, in an iframe, it takes a bit for the modal to
                // position itself, so if the modal is opened and focused immediately, the focus will
                // cause the page to scroll to the top. So we override and eliminate the autofocus behavior
                // by placing the ngbAutofocus attribute on the modal header, which is not focusable.
                // Then we wait 1 second for the modal to be positioned properly and focus the first
                // focusable element in the modal ourselves
                S25Util.isInIframe &&
                    setTimeout(() => {
                        let focusableElem = opened && jSith.focusable("ngb-modal-window", 0, false, false);
                        focusableElem && focusableElem.focus();
                    }, 1000);

                modalRef.result.finally(() => {
                    opened = false;
                    ModalService.returnToFocusElement && ModalService.returnToFocusElement.focus();
                    resolve();
                    ModalService.unregisterModal({ close: modalRef.close, closed: promise });
                });

                return modalRef.result;
            });
        });
    }

    public static async queueModal<T>(modalType: ModalName, data?: T) {
        await Promise.allSettled(this.openModals.map((modal) => modal.closed)); // Wait for any open modal not in queue
        const q = this.queue.slice(); // Copy queue
        const { promise, resolve, reject } = S25Util.createPromise();
        this.queue.push(promise);
        await Promise.allSettled(q); // Wait for other modals
        const ret = await this.modal(modalType, data);
        this.queue.shift(); // Pop own promise
        resolve();
        return ret;
    }

    public static closeAll() {
        ModalService.ngbModal?.dismissAll();
        // Closing the modal will automatically unregister it via promise.finally()
        for (const modal of ModalService.openModals) modal.close();
    }

    public static dialogType = function (type: string, data: any, primary?: string) {
        data = data || {};
        let options = type.split(" ");
        data.buttonMap = {};
        jSith.forEach(options, function (_, val) {
            switch (val) {
                case "Yes":
                    data.buttonMap.hasYes = 1;
                    break;
                case "No":
                    data.buttonMap.hasNo = 1;
                    break;
                case "Cancel":
                    data.buttonMap.hasCancel = 1;
                    break;
                case "Continue":
                    data.buttonMap.hasContinue = 1;
                    break;
                case "OK":
                    data.buttonMap.hasOK = 1;
                    break;
                default:
                    break;
            }
        });
        data.buttonMap.primary = primary;
        return data;
    };

    s25Modal<CloseData extends any = any, TemplateContext extends object = any>(
        config: ModalConfig<CloseData, TemplateContext>,
    ): Modal<CloseData> {
        let componentElem: HTMLElement;
        let componentRef: ComponentRef<S25ModalComponent<CloseData, TemplateContext>>;
        const closedPromise = S25Util.createPromise<CloseData>();
        const close = (data?: CloseData) => {
            document.body.removeChild(componentRef.location.nativeElement);
            this.appRef.detachView(componentRef.hostView);
            componentRef.destroy();
            closedPromise.resolve(data);
        };
        componentRef = createComponent(S25ModalComponent<CloseData, TemplateContext>, {
            environmentInjector: this.appRef.injector,
        });
        componentElem = (<EmbeddedViewRef<unknown>>componentRef.hostView).rootNodes[0];
        document.body.append(componentElem);
        this.appRef.attachView(componentRef.hostView);

        const modalPromise = componentRef.instance.open(config);
        modalPromise.then(closedPromise.resolve, closedPromise.reject);

        const modal: Modal<CloseData> = { close, closed: closedPromise.promise };
        ModalService.registerModal(modal);
        closedPromise.promise.finally(() => ModalService.unregisterModal(modal));
        return modal;
    }
}

type Modal<CloseData = unknown> = { close: (data?: CloseData) => unknown; closed: Promise<CloseData> };
